import React, { useRef, useEffect, useState, useCallback, useMemo, forwardRef, useImperativeHandle, } from 'react';
import Webcam from 'react-webcam';
import { FaceMesh } from '@mediapipe/face_mesh';
import { useStatsSettings } from '../context/Context';

// ── Oval guide drawn on canvas (pre-monitoring: static white; during monitoring: colour-coded) ──
function drawFaceGuideOval(
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  faceLandmarks: { x: number; y: number }[] | null,
  mirrored = false,
) {
  const cx = W * 0.5;
  const cy = H * 0.43;
  const rx = W * 0.22;
  const ry = Math.min(H * 0.34, rx * 1.5); // cap on portrait so oval stays face-shaped

  let borderColor = 'rgba(255,255,255,0.75)';
  let glowColor   = 'transparent';
  let guideText   = '';

  if (faceLandmarks) {
    const xs = faceLandmarks.map(lm => lm.x * W);
    const ys = faceLandmarks.map(lm => lm.y * H);
    const faceCx   = (Math.min(...xs) + Math.max(...xs)) / 2;
    const faceCy   = (Math.min(...ys) + Math.max(...ys)) / 2;
    const faceW    = Math.max(...xs) - Math.min(...xs);
    const faceH    = Math.max(...ys) - Math.min(...ys);
    const avgFill  = (faceW / (2 * rx) + faceH / (2 * ry)) / 2;
    const centered = Math.abs(faceCx - cx) < W * 0.15 && Math.abs(faceCy - cy) < H * 0.15;

    if (!centered) {
      borderColor = 'rgba(251,191,36,0.95)';
      guideText   = 'Centre your face';
    } else if (avgFill < 0.65) {
      borderColor = 'rgba(96,165,250,0.95)';
      guideText   = 'Move closer';
    } else if (avgFill > 1.3) {
      borderColor = 'rgba(251,146,60,0.95)';
      guideText   = 'Move back';
    } else {
      borderColor = 'rgba(74,222,128,0.98)';
      glowColor   = 'rgba(74,222,128,0.45)';
    }
  }

  // Dark vignette outside oval (nonzero winding cutout)
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, W, H);
  ctx.ellipse(cx, cy, rx, ry, 0, Math.PI * 2, 0, true); // counterclockwise = hole
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.fill();

  // Oval border + optional glow
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.strokeStyle = borderColor;
  ctx.lineWidth   = 3;
  if (glowColor !== 'transparent') {
    ctx.shadowColor = glowColor;
    ctx.shadowBlur  = 14;
  }
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Corner tick marks (top-left, top-right, bottom-left, bottom-right of oval)
  const tickLen = Math.min(W, H) * 0.04;
  const corners: [number, number, number][] = [
    [cx - rx, cy, -Math.PI / 2],
    [cx + rx, cy, Math.PI / 2],
    [cx, cy - ry, 0],
    [cx, cy + ry, Math.PI],
  ];
  ctx.strokeStyle = borderColor;
  ctx.lineWidth   = 3;
  corners.forEach(([x, y, _]) => {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y);
  });

  // Guide text below oval — un-mirror when canvas is CSS-flipped so text reads correctly
  if (guideText) {
    const textY = cy + ry + 18;
    ctx.save();
    if (mirrored) {
      ctx.scale(-1, 1);
      ctx.translate(-W, 0);
    }
    ctx.font         = `bold ${Math.round(H * 0.028)}px -apple-system, BlinkMacSystemFont, sans-serif`;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle    = borderColor;
    // When mirrored the visual centre maps to (W - cx) in the flipped coordinate space
    ctx.fillText(guideText, mirrored ? W - cx : cx, textY);
    ctx.restore();
  }

  ctx.restore();
}


export type VideoQualityWarning =
  | 'face_too_small'
  | 'face_clipped'
  | 'excessive_movement'
  | 'low_resolution'
  | 'low_brightness'
  | 'noisy_signal'
  | 'hr_out_of_range';

interface WebcamCardProps {
  source?: 'webcam' | 'video';
  videoSrc?: string;
  mode?: 'download' | 'api' | 'stream';
  hideControls?: boolean;
  onApiResponse?: (result: { SBP: number[] | null; DBP: number[] | null, PPG: number[], HR: number, RR: number, HRV: number, ECG: number[], ICG?: number[], RESP?: number[], BP_wave?: number[], SpO2?: number | null, buffer_frames?: number, signal_quality?: { snr_db: number, warnings: string[] } }) => void;
  onProcessingStopped?: (stopped: boolean) => void;
  onApiError?: (message: string) => void;
  onMediaPipeError?: (failed: boolean) => void;
  onInferenceProgress?: (progress: number, framesCollected: number, framesRequired: number) => void;
  onQualityWarning?: (warnings: VideoQualityWarning[]) => void;
  // How stale the most recently displayed result is, in ms — computed as
  // (time the result arrived) - (capture timestamp of the last frame that
  // went into producing it). Reported once per result, not throttled.
  onLatencyMs?: (ms: number) => void;
  // Actual achieved frame-processing rate (MediaPipe results/sec), not the
  // camera's nominal/requested rate — reflects real device + main-thread
  // conditions. Reported about once per second.
  onCameraFps?: (fps: number) => void;
}

interface FrameData {
  timestamp: number;
  rgbValues?: { r: number; g: number; b: number }[];
  faceCrop?: string;
}


export interface WebcamCardHandle {
  toggleInference: () => void;
  sendToApi: (data: { frames: { timestamp: number; landmarks?: number[][]; rgbValues?: { r: number; g: number; b: number }[] }[] }) => void;
  // Null when no webcam track exists (video-file source) or the browser
  // doesn't support the Media Capture Track Capabilities API at all —
  // callers must check before showing any control, never assume a field
  // is present just because the method returned non-null.
  getVideoTrackCapabilities: () => MediaTrackCapabilities | null;
  applyVideoConstraints: (constraints: MediaTrackConstraints) => Promise<void>;
  // Records the raw camera feed for the whole session as one continuous
  // clip — separate from the legacy 1s-segment MediaRecorder above (that
  // one streams short chunks over a raw WebSocket for a different, unused
  // "stream" mode and must not be touched by this). No-op if the browser
  // doesn't support MediaRecorder or there's no live video track yet.
  startSessionRecording: () => void;
  // Resolves with the full-session clip, or null if no recording was
  // active (startSessionRecording was never called, or nothing recorded).
  stopSessionRecording: () => Promise<Blob | null>;
}

const WebcamCard = forwardRef<WebcamCardHandle, WebcamCardProps>(
  ({ source = 'webcam', videoSrc = '', mode = 'download', hideControls = false, onApiResponse, onProcessingStopped, onApiError, onMediaPipeError, onInferenceProgress, onQualityWarning, onLatencyMs, onCameraFps }, ref) => {
  const webcamRef = useRef<Webcam>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const faceMeshRef = useRef<FaceMesh | null>(null);
  const handleResultsRef = useRef<(results: any) => void>(() => {});
  // Lag/FPS instrumentation — see onLatencyMs/onCameraFps above.
  const lastWsFrameTimestampRef = useRef<number>(0);
  const fpsFrameCountRef = useRef<number>(0);
  const fpsWindowStartRef = useRef<number>(0);

  const { selectedModel, rhythmNetTransport, rhythmNetHopFrames, rhythmNetBufferWindows, rhythmNetDenoiseEnabled } = useStatsSettings();

  const framesChunkRef = useRef<FrameData[]>([]);
  const slideCountRef = useRef<number>(0);
  const selectedModelLower = selectedModel.toLowerCase();
  const isDeepPhysModel = selectedModelLower.includes('deepphys');
  const isPhysFormerModel = selectedModelLower.includes('physformer');
  const isBPFormerModel = selectedModelLower.includes('bpformer');
  // RhythmNet (Navid, exp_32): 160 frames @30fps, 128×128 RGB face crop at 1.5× the landmark extent
  const isRhythmNetModel = selectedModelLower.includes('rhythmnet');
  const isHiResCropModel = isPhysFormerModel || isRhythmNetModel;
  const isSeeVitaModel = selectedModelLower.includes('seevita') || isDeepPhysModel || isPhysFormerModel || isBPFormerModel || isRhythmNetModel;
  const chunkSize = isBPFormerModel ? 300 : isRhythmNetModel ? 160 : isPhysFormerModel ? 160 : isDeepPhysModel ? 180 : isSeeVitaModel ? 180 : 50;
  const RN_WINDOW_SECONDS = 160 / 30; // RhythmNet's fixed window size at 30fps
  const cropSize = (isBPFormerModel || isHiResCropModel) ? 128 : 72;
  // PhysFormer/BPFormer/RhythmNet spec: scale mediapipe bbox by 1.5× (25% pad on each side)
  const cropPadFactor = (isBPFormerModel || isHiResCropModel) ? 0.25 : 0.15;
  // Lets the server stitch this scan's overlapping windows into one timeline (HR/RR/BP over 20–60 s)
  const scanSessionIdRef = useRef<string>(`${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);
  // RhythmNet's send cadence is user-configurable (Settings → Update frequency);
  // every other model keeps the original fixed interval.
  const SLIDE_INTERVAL = isRhythmNetModel ? rhythmNetHopFrames : 10;
  const reportInferenceProgress = useCallback((framesCollected: number) => {
    if (!onInferenceProgress) return;
    const clamped = Math.max(0, Math.min(framesCollected, chunkSize));
    if (clamped !== 0 && clamped !== chunkSize && Math.abs(clamped - lastReportedFrameRef.current) < 5) return;
    lastReportedFrameRef.current = clamped;
    onInferenceProgress(Math.round((clamped / chunkSize) * 100), clamped, chunkSize);
  }, [chunkSize, onInferenceProgress]);

  const socketRef = useRef<WebSocket | null>(null);
  // RhythmNet streaming mode — separate socket from the legacy landmarks `socketRef`
  const rnWsRef = useRef<WebSocket | null>(null);
  const rnTransportActiveRef = useRef<'http' | 'ws'>('http');
  const rnWsOpenRef = useRef(false);
  const rnLastMessageAtRef = useRef<number>(0);
  const rnParseFailuresRef = useRef(0);
  const rnProcessingStartedAtRef = useRef<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const sessionRecorderRef = useRef<MediaRecorder | null>(null);
  const sessionChunksRef = useRef<BlobPart[]>([]);
  const sessionStopResolveRef = useRef<((blob: Blob | null) => void) | null>(null);
  const inferInFlightRef = useRef(0);
  const MAX_INFLIGHT = 1;
  const pendingChunkRef = useRef<FrameData[] | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const pendingCallbacksRef = useRef<Map<string, { resolve: (r: any) => void; reject: (e: Error) => void }>>(new Map());

  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [stoppedDueToNoFace, setStoppedDueToNoFace] = useState(false);
  // RhythmNet assumes a constant 30fps throughout (HR via Welch spectral
  // peak, RR/HR bandpass filters, the window-fusion weights) — a live
  // webcam's actual delivered rate can fluctuate well below that under
  // lighting changes (longer exposure per frame) with the previous
  // constraint ({ideal:30, max:30}, no floor) never objecting. A recorded
  // video upload has no such fluctuation, which is the confirmed cause of
  // "signal is cleaner from an uploaded video than the live browser feed"
  // (same pre/post-processing code either way). min:24 pushes the browser
  // to reject frame-rate-starved conditions instead of silently degrading;
  // falls back to the old unconstrained behavior only if that's rejected
  // outright (better a working noisy camera than no camera at all).
  const [strictFrameRate, setStrictFrameRate] = useState(true);
  const [recordedData, setRecordedData] = useState<{
      frames: {
        timestamp: number;
        landmarks?: number[][];
        rgbValues?: { r: number; g: number; b: number }[];
      }[];
    }>({ frames: [] });
  const [recordingTime, setRecordingTime] = useState(0);
  const [fps, setFps] = useState<number>(0);
  const startTimeRef = useRef<number>(0);
  const frameCountRef = useRef<number>(0);
  const noFaceFrameCountRef = useRef<number>(0);

  const isRecordingRef = useRef(isRecording);
  const isProcessingRef = useRef(isProcessing);
  const sendToApiRef = useRef<((data: typeof recordedData) => Promise<void>) | null>(null);

  const tempCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const cropCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // EMA state for crop-box stabilisation: {cx, cy, side} in video pixels
  const cropEmaRef = useRef<{cx: number; cy: number; side: number} | null>(null);
  const lastFaceMeshSendRef = useRef<number>(0);
  const lastReportedFrameRef = useRef<number>(-1);
  const videoRef = useRef<HTMLVideoElement>(null);
  const faceMeshReinitializingRef = useRef(false);
  const prevLandmarksRef = useRef<{x: number; y: number}[] | null>(null);
  const qualityWarningsRef = useRef<VideoQualityWarning[]>([]);
  const lastQualityReportRef = useRef(0);

  const initializeFaceMesh = useCallback(async () => {
    try {
      // Close any existing broken instance before creating a new one
      if (faceMeshRef.current) {
        try { faceMeshRef.current.close(); } catch { /* ignore */ }
        faceMeshRef.current = null;
      }
      const faceMesh = new FaceMesh({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
      });

      faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: false,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });

      faceMesh.onResults((results) => {
        try {
          handleResultsRef.current(results);
        } catch (error) {
          console.error('Error handling results:', error);
        }
      });

      faceMeshRef.current = faceMesh;
    } catch (error) {
      console.error('Error initializing FaceMesh:', error);
    }
  }, []);

  const sendFrameToFaceMesh = useCallback(async (video: HTMLVideoElement) => {
    try {
      const now = performance.now();
      // Grid-locked 30fps throttle — advance the grid target by exactly 33ms
      // so late frames don't accumulate drift (unlike `lastRef = now`).
      if (now >= lastFaceMeshSendRef.current + 33) {
        if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0 && faceMeshRef.current) {
          lastFaceMeshSendRef.current = lastFaceMeshSendRef.current + 33;
          await faceMeshRef.current.send({ image: video });
        }
      }
      if (isProcessingRef.current) {
        requestAnimationFrame(() => {
          void sendFrameToFaceMesh(video);
        });
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      // WASM abort — the MediaPipe module crashed (usually a slow/interrupted CDN load).
      // Reinitialize and resume without stopping the session.
      if ((msg.includes('abort') || error instanceof (window as any).RuntimeError) && !faceMeshReinitializingRef.current) {
        faceMeshReinitializingRef.current = true;
        faceMeshRef.current = null;
        try {
          await initializeFaceMesh();
        } finally {
          faceMeshReinitializingRef.current = false;
        }
        if (isProcessingRef.current) {
          requestAnimationFrame(() => void sendFrameToFaceMesh(video));
        }
      } else if (!faceMeshReinitializingRef.current) {
        console.error('Error sending frame to FaceMesh:', error);
      }
    }
  }, [initializeFaceMesh]);


  const startWebSocketConnection = () => {
    if (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED) {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      socketRef.current = new WebSocket(`${wsProtocol}://${window.location.host}/ws`);
  
      socketRef.current.onopen = () => {
        console.log('WebSocket connection established.');
      };
  
      socketRef.current.onmessage = (event) => {
        console.log('Message from server:', event.data);
      };
  
      socketRef.current.onclose = (event) => {
        console.log('WebSocket connection closed.', event.reason || 'No reason provided.');
      };
  
      socketRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    }
  };
  
  const stopWebSocketConnection = () => {
    if (socketRef.current && socketRef.current.readyState !== WebSocket.CLOSED) {
      console.log('Closing WebSocket connection...');
      socketRef.current.close();
      socketRef.current = null;
    }
  };

  // Settings → Initial buffer: hold back results until this many full
  // 160-frame windows' worth of real time have elapsed since processing
  // started, trading a longer wait-before-first-reading for a steadier one.
  // Only meaningful for RhythmNet; every other model forwards immediately.
  const forwardApiResponse = useCallback((result: any) => {
    if (isRhythmNetModel && rhythmNetBufferWindows > 1) {
      const bufferMs = rhythmNetBufferWindows * (RN_WINDOW_SECONDS * 1000);
      if (Date.now() - rnProcessingStartedAtRef.current < bufferMs) return;
    }
    onApiResponse?.(result);
  }, [onApiResponse, rhythmNetBufferWindows, isRhythmNetModel, RN_WINDOW_SECONDS]);

  // Opens the RhythmNet streaming socket. Falls back to the HTTP batched path
  // (rnTransportActiveRef.current = 'http') if it never opens or drops mid-session —
  // handleResults checks that ref per-frame and the existing chunk/slide HTTP
  // logic below takes over untouched, so RhythmNet never stops reporting results.
  //
  // Two failure modes beyond a clean close/error are guarded against here,
  // since a server-side bug (or an unlucky NaN in the signal) can leave the
  // socket technically open but silently dead — no close/error event ever
  // fires, so without this the session would just freeze with no recovery:
  //  1. A message that fails to parse (e.g. a stray non-JSON token) counts
  //     as a strike; too many in a row forces the fallback.
  //  2. A watchdog checks that *some* message has arrived recently relative
  //     to the current hop setting; if not, it forces the fallback too.
  const startRhythmNetStream = useCallback(() => {
    rnTransportActiveRef.current = 'ws';
    rnWsOpenRef.current = false;
    rnLastMessageAtRef.current = Date.now();
    rnParseFailuresRef.current = 0;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${wsProtocol}://${window.location.host}/ws/rhythmnet`);
    rnWsRef.current = ws;

    const fallbackToHttp = (reason: string) => {
      if (rnTransportActiveRef.current === 'http') return;
      console.warn(`RhythmNet stream ${reason} — falling back to HTTP batched inference.`);
      rnTransportActiveRef.current = 'http';
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close();
    };

    ws.onopen = () => {
      rnWsOpenRef.current = true;
      rnLastMessageAtRef.current = Date.now();
      ws.send(JSON.stringify({ session_id: scanSessionIdRef.current, hop: rhythmNetHopFrames, denoise: rhythmNetDenoiseEnabled }));
    };
    ws.onmessage = (event) => {
      rnLastMessageAtRef.current = Date.now();
      try {
        const result = JSON.parse(event.data);
        rnParseFailuresRef.current = 0;
        if (result && !result.error) {
          forwardApiResponse(result);
          // Approximation: assumes near-1:1 send/receive cadence, true at
          // hop=1 — not a precise per-frame round trip, but a good proxy for
          // "how stale is what's on screen right now."
          if (onLatencyMs && lastWsFrameTimestampRef.current > 0) {
            onLatencyMs(Date.now() - lastWsFrameTimestampRef.current);
          }
        }
      } catch (e) {
        rnParseFailuresRef.current += 1;
        console.error('Error parsing RhythmNet stream message:', e);
        if (rnParseFailuresRef.current >= 3) fallbackToHttp('sent unparseable messages repeatedly');
      }
    };
    ws.onerror = () => fallbackToHttp('error');
    ws.onclose = () => {
      rnWsOpenRef.current = false;
      if (isProcessingRef.current) fallbackToHttp('closed unexpectedly');
    };
  }, [forwardApiResponse, rhythmNetHopFrames, rhythmNetDenoiseEnabled, onLatencyMs]);

  const stopRhythmNetStream = useCallback(() => {
    rnWsOpenRef.current = false;
    if (rnWsRef.current && rnWsRef.current.readyState !== WebSocket.CLOSED) {
      rnWsRef.current.close();
    }
    rnWsRef.current = null;
  }, []);

  // Watchdog: a connection can go silently dead (no close/error event) without
  // this — e.g. a server-side bug that stops a specific connection's loop.
  // Threshold scales with hop: a large hop legitimately waits longer between
  // results, so judge staleness against that, not a fixed short timeout.
  useEffect(() => {
    if (!isProcessing || !isRhythmNetModel || rnTransportActiveRef.current !== 'ws') return;
    const expectedGapMs = (rhythmNetHopFrames / 30) * 1000;
    const staleAfterMs = Math.max(8000, expectedGapMs * 4);
    const interval = setInterval(() => {
      if (rnTransportActiveRef.current !== 'ws') return;
      if (Date.now() - rnLastMessageAtRef.current > staleAfterMs) {
        console.warn(`RhythmNet stream silent for >${Math.round(staleAfterMs / 1000)}s — falling back to HTTP batched inference.`);
        rnTransportActiveRef.current = 'http';
        if (rnWsRef.current) {
          if (rnWsRef.current.readyState === WebSocket.OPEN || rnWsRef.current.readyState === WebSocket.CONNECTING) {
            rnWsRef.current.close();
          }
        }
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [isProcessing, isRhythmNetModel, rhythmNetHopFrames]);


  
  const startVideoStreaming = () => {
    let videoStream: MediaStream | null = null;
  
    // Determine the source and retrieve the appropriate video stream
    if (source === 'webcam') {
      videoStream = webcamRef.current?.video?.srcObject as MediaStream;
    } else if (source === 'video' && videoRef.current) {
      const videoElement = videoRef.current;
  
      // Check if `captureStream` is available on the video element
      if ('captureStream' in videoElement) {
        videoStream = (videoElement as any).captureStream() || null;
  
        // Play the video if paused
        if (videoElement.paused) {
          videoElement.play().catch((error) => {
            console.error('Error playing the video:', error);
          });
        }
      } else {
        console.error('captureStream is not supported in this browser.');
      }
    }
  
    // Start MediaRecorder if the videoStream is available
    if (videoStream) {
      let chunks: BlobPart[] = [];
      const mediaRecorder = new MediaRecorder(videoStream, { mimeType: 'video/webm; codecs=vp8' });
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
    
      mediaRecorder.start();
      setTimeout(() => {
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.stop();
        }
      }, 1000);
    
      mediaRecorder.onstop = () => {
        const completeBlob = new Blob(chunks, { type: 'video/webm' });
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          socketRef.current.send(completeBlob);
        }
        chunks.length = 0;
    
        // Restart recording for continuous 1-second segments
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.start();
          setTimeout(() => {
            if (mediaRecorderRef.current) {
              mediaRecorderRef.current.stop();
            }
          }, 1000);
        }
      };
    } else {
      console.error('No valid video stream available.');
    }
    
    
  };
  
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (sessionRecorderRef.current && sessionRecorderRef.current.state !== 'inactive') {
        sessionRecorderRef.current.stop();
      }
      stopVideoStreaming();
      stopWebSocketConnection();
      stopRhythmNetStream();
    };
  }, []);
  

  useEffect(() => {
    if (mode !== 'stream') {
      // If we are no longer in `stream` mode, ensure we close any active connections
      stopWebSocketConnection();
      stopVideoStreaming();
    }
  }, [mode]);
  
  const stopVideoStreaming = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
  };

  
  useEffect(() => {
    initializeFaceMesh();
  }, [initializeFaceMesh]);

  // Persistent Web Worker — keeps JSON.stringify + fetch off the main thread
  useEffect(() => {
    const worker = new Worker(new URL('../workers/inferenceWorker.ts', import.meta.url));
    worker.onmessage = (e: MessageEvent) => {
      const { requestId, result, error } = e.data;
      const cb = pendingCallbacksRef.current.get(requestId);
      if (!cb) return;
      pendingCallbacksRef.current.delete(requestId);
      if (error) cb.reject(new Error(error));
      else cb.resolve(result);
    };
    workerRef.current = worker;
    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  // Pre-monitoring oval guide — draw the static oval on the canvas while not processing
  useEffect(() => {
    if (isProcessing) return; // FaceMesh takes over once monitoring starts
    let animId: number;
    const draw = () => {
      if (isProcessingRef.current) return; // hand off cleanly
      const video = source === 'webcam' ? webcamRef.current?.video : videoRef.current;
      const canvas = canvasRef.current;
      if (canvas && video && video.readyState >= 2 && video.videoWidth > 0) {
        const dW = canvas.offsetWidth  || video.videoWidth;
        const dH = canvas.offsetHeight || video.videoHeight;
        canvas.width  = dW;
        canvas.height = dH;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (ctx) {
          ctx.clearRect(0, 0, dW, dH);
          drawFaceGuideOval(ctx, dW, dH, null, source === 'webcam');
        }
      }
      animId = requestAnimationFrame(draw);
    };
    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, [isProcessing, source]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
    if (!isRecording) {
      setRecordingTime(0); // Reset timer when recording stops
    }
  }, [isRecording]);

  useEffect(() => {
    if (!isRecording) return;
  
    let startTime = performance.now();
  
    const calculateFps = setInterval(() => {
      const now = performance.now();
      const elapsedTime = (now - startTime) / 1000; // Time in seconds
      if (elapsedTime > 0) {
        setFps(parseFloat((frameCountRef.current / elapsedTime).toFixed(2)));
      }
      frameCountRef.current = 0; // Reset frame count
      startTime = now; // Reset start time
    }, 1000); // Update FPS every second
  
    return () => clearInterval(calculateFps);
  }, [isRecording]);
  
  

  useEffect(() => {
    isProcessingRef.current = isProcessing;
    if (isProcessing) {
      // Reset no-face counter and stopped flag when processing starts
      noFaceFrameCountRef.current = 0;
      setStoppedDueToNoFace(false);
      onProcessingStopped?.(false); // Notify parent that processing resumed
      reportInferenceProgress(0);
      pendingChunkRef.current = null;
      rnProcessingStartedAtRef.current = Date.now();
      // Anchor the FaceMesh send-throttle's grid to *now*, not its 0 default.
      // sendFrameToFaceMesh only gates on `now >= lastFaceMeshSendRef.current
      // + 33`, so leaving it at 0 means that check trivially passes on every
      // single rAF tick for as long as it takes the +33-per-frame grid to
      // climb from 0 up to performance.now()'s actual (large, since-page-load)
      // value — hundreds of ticks, i.e. several real seconds of effectively
      // *no* throttling, feeding the model frames at the display's full
      // refresh rate instead of the intended ~30fps right at the start of
      // every single session (confirmed live: ~57fps instead of ~30fps on
      // the on-screen camera-fps readout). Resetting it here means the grid
      // starts already caught up, so the 30fps cap is enforced from the very
      // first frame.
      lastFaceMeshSendRef.current = performance.now();
      if (isRhythmNetModel && rhythmNetTransport === 'ws') {
        startRhythmNetStream();
      } else {
        rnTransportActiveRef.current = 'http';
      }
      let videoElement: HTMLVideoElement | null = null;

      if (source === 'webcam') {
        videoElement = webcamRef.current?.video || null;
      } else if (source === 'video') {
        videoElement = videoRef.current || null;
        videoElement?.play().catch(() => {});
      }

      if (videoElement && faceMeshRef.current) {
        sendFrameToFaceMesh(videoElement);
      }

      return () => {};
    } else {
      stopRhythmNetStream();
      framesChunkRef.current = [];
      slideCountRef.current = 0;
      pendingChunkRef.current = null;
      cropEmaRef.current = null;
      reportInferenceProgress(0);
      fpsFrameCountRef.current = 0;
      fpsWindowStartRef.current = 0;
    }
  }, [isProcessing, isRhythmNetModel, isSeeVitaModel, onProcessingStopped, reportInferenceProgress, rhythmNetTransport, sendFrameToFaceMesh, source, startRhythmNetStream, stopRhythmNetStream]);


  

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null; // Initialize as null
  
    if (isRecording) {
      const startTime = performance.now(); // Start time in milliseconds
      timer = setInterval(() => {
        const elapsedTime = (performance.now() - startTime) / 1000; // Convert to seconds
        setRecordingTime(elapsedTime);
      }, 100); // Update every 100ms for precision
    } else if (timer) {
      clearInterval(timer);
    }
  
    return () => {
      if (timer) clearInterval(timer); // Ensure cleanup
    };
  }, [isRecording]);


  
  
  


  const sessionRecordingMime = useMemo(() => [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ].find((t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)), []);

  const startSessionRecording = useCallback(() => {
    const stream = source === 'webcam'
      ? (webcamRef.current?.video?.srcObject as MediaStream | null)
      : (videoRef.current && 'captureStream' in videoRef.current
          ? (videoRef.current as any).captureStream()
          : null);
    if (!stream || !sessionRecordingMime) return;
    try {
      const recorder = new MediaRecorder(stream, { mimeType: sessionRecordingMime });
      sessionChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) sessionChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = sessionChunksRef.current.length > 0
          ? new Blob(sessionChunksRef.current, { type: sessionRecordingMime })
          : null;
        sessionChunksRef.current = [];
        sessionStopResolveRef.current?.(blob);
        sessionStopResolveRef.current = null;
      };
      recorder.start(1000); // 1s timeslices so ondataavailable fires incrementally
      sessionRecorderRef.current = recorder;
    } catch {
      sessionRecorderRef.current = null;
    }
  }, [source, sessionRecordingMime]);

  const stopSessionRecording = useCallback((): Promise<Blob | null> => {
    const recorder = sessionRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return Promise.resolve(null);
    return new Promise((resolve) => {
      sessionStopResolveRef.current = resolve;
      recorder.stop();
      sessionRecorderRef.current = null;
    });
  }, []);

  useImperativeHandle(ref, () => ({
    toggleInference,
    sendToApi,
    startSessionRecording,
    stopSessionRecording,
    getVideoTrackCapabilities: () => {
      const stream = webcamRef.current?.video?.srcObject as MediaStream | null;
      const track = stream?.getVideoTracks?.()[0];
      if (!track || typeof track.getCapabilities !== 'function') return null;
      try {
        return track.getCapabilities();
      } catch {
        return null;
      }
    },
    applyVideoConstraints: async (constraints: MediaTrackConstraints) => {
      const stream = webcamRef.current?.video?.srcObject as MediaStream | null;
      const track = stream?.getVideoTracks?.()[0];
      if (!track) return;
      await track.applyConstraints({ advanced: [constraints] });
    },
  }));
    

  const sendToApi = useCallback(async (data: typeof recordedData) => {
    const requestFrames = (data.frames || []) as FrameData[];
    if (inferInFlightRef.current >= MAX_INFLIGHT) {
      pendingChunkRef.current = requestFrames;
      return;
    }
    inferInFlightRef.current++;
    try {
      const requestId = `${Date.now()}-${Math.random()}`;
      // Post to worker — JSON.stringify + fetch run off the main thread
      const result = await new Promise<any>((resolve, reject) => {
        pendingCallbacksRef.current.set(requestId, { resolve, reject });
        workerRef.current?.postMessage({ requestId, frames: requestFrames, modelName: selectedModel, sessionId: scanSessionIdRef.current, denoise: rhythmNetDenoiseEnabled });
      });
      forwardApiResponse(result);
      const lastFrameTimestamp = requestFrames[requestFrames.length - 1]?.timestamp;
      if (onLatencyMs && lastFrameTimestamp) onLatencyMs(Date.now() - lastFrameTimestamp);
    } catch (error) {
      console.error('Error sending data to API:', error);
      const message = error instanceof Error ? error.message : 'Failed to connect to the inference API.';
      onApiError?.(message);
    } finally {
      inferInFlightRef.current = Math.max(0, inferInFlightRef.current - 1);
      if (isProcessingRef.current && pendingChunkRef.current && inferInFlightRef.current < MAX_INFLIGHT && sendToApiRef.current) {
        const queued = pendingChunkRef.current;
        pendingChunkRef.current = null;
        void sendToApiRef.current({ frames: queued });
      }
    }
  }, [onApiError, forwardApiResponse, selectedModel, onLatencyMs, rhythmNetDenoiseEnabled]);
  sendToApiRef.current = sendToApi;
  

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isProcessing && !isSeeVitaModel) {
      interval = setInterval(() => {
        const chunk = framesChunkRef.current.splice(0, chunkSize);
        if (chunk.length > 0) {
          sendToApi({ frames: chunk });
          reportInferenceProgress(framesChunkRef.current.length);
        }
      }, 2000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [chunkSize, isProcessing, isSeeVitaModel, reportInferenceProgress, sendToApi]);
  
  

const handleResults = (results: any) => {
  try {
    // Camera FPS: this fires once per actually-processed frame (every
    // FaceMesh result while a scan is running), so counting calls per
    // second gives the real achieved rate — not the camera's requested
    // rate, which main-thread contention can fall well short of.
    if (onCameraFps) {
      const now = performance.now();
      if (fpsWindowStartRef.current === 0) fpsWindowStartRef.current = now;
      fpsFrameCountRef.current += 1;
      const elapsed = now - fpsWindowStartRef.current;
      if (elapsed >= 1000) {
        onCameraFps(Math.round((fpsFrameCountRef.current / elapsed) * 1000));
        fpsFrameCountRef.current = 0;
        fpsWindowStartRef.current = now;
      }
    }

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d', { willReadFrequently: true });
    const video = source === 'webcam' ? webcamRef.current?.video : videoRef.current;


    if (!canvas || !ctx || !video) return;

    // Use display container size so canvas never distorts when video aspect ≠ container aspect.
    // The <video> element handles display correctly via objectFit:cover; canvas is overlay only.
    const dispW = canvas.offsetWidth  || video.videoWidth;
    const dispH = canvas.offsetHeight || video.videoHeight;
    canvas.width  = dispW;
    canvas.height = dispH;

    // objectFit:cover transform — map video pixel coords → canvas display coords
    const vW = video.videoWidth, vH = video.videoHeight;
    const cvScale  = Math.max(dispW / vW, dispH / vH);
    const cvOffX   = (dispW - vW * cvScale) / 2;
    const cvOffY   = (dispH - vH * cvScale) / 2;

    if (!tempCanvasRef.current) tempCanvasRef.current = document.createElement('canvas');
    const tempCanvas = tempCanvasRef.current;
    tempCanvas.width = video.videoWidth;
    tempCanvas.height = video.videoHeight;
    const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
    tempCtx?.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);

    if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
      // Face detected - reset the no-face counter
      noFaceFrameCountRef.current = 0;

      // Select only the first face's landmarks
      const faceLandmarks = results.multiFaceLandmarks[0];

      // ── Quality checks #4 #5 #6 #7 ────────────────────────────────────────
      if (isProcessingRef.current && onQualityWarning) {
        const W = video.videoWidth, H = video.videoHeight;
        const warnings: VideoQualityWarning[] = [];

        // #6 low resolution
        if (W < 320 || H < 240) warnings.push('low_resolution');

        // #4 face size and clipping
        const xs = faceLandmarks.map((lm: any) => lm.x * W);
        const ys = faceLandmarks.map((lm: any) => lm.y * H);
        const faceW = Math.max(...xs) - Math.min(...xs);
        const faceH = Math.max(...ys) - Math.min(...ys);
        const faceFraction = (faceW * faceH) / (W * H);
        if (faceFraction < 0.04) warnings.push('face_too_small');
        const margin = 5;
        if (Math.min(...xs) < margin || Math.max(...xs) > W - margin ||
            Math.min(...ys) < margin || Math.max(...ys) > H - margin)
          warnings.push('face_clipped');

        // #5 excessive movement — track nose tip (landmark 1) and chin (152)
        const keyPoints = [1, 152, 234, 454].map((i) => ({
          x: faceLandmarks[i]?.x ?? 0,
          y: faceLandmarks[i]?.y ?? 0,
        }));
        if (prevLandmarksRef.current) {
          const dists = keyPoints.map((pt, i) => {
            const prev = prevLandmarksRef.current![i];
            return Math.hypot(pt.x - prev.x, pt.y - prev.y);
          });
          const maxDist = Math.max(...dists);
          if (maxDist > 0.04) warnings.push('excessive_movement'); // >4% of frame width
        }
        prevLandmarksRef.current = keyPoints;

        // #7 brightness — sample face centre patch via tempCtx
        if (tempCtx) {
          const cx = Math.round((Math.min(...xs) + Math.max(...xs)) / 2);
          const cy = Math.round((Math.min(...ys) + Math.max(...ys)) / 2);
          const patchSize = Math.max(1, Math.round(faceW * 0.2));
          const px = Math.max(0, cx - patchSize / 2), py = Math.max(0, cy - patchSize / 2);
          const pw = Math.min(patchSize, W - px), ph = Math.min(patchSize, H - py);
          if (pw > 0 && ph > 0) {
            const imageData = tempCtx.getImageData(px, py, pw, ph).data;
            let brightness = 0;
            for (let i = 0; i < imageData.length; i += 4)
              brightness += (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
            brightness /= (imageData.length / 4);
            if (brightness < 50) warnings.push('low_brightness');
          }
        }

        // Report at most once per second to avoid hammering the parent
        const now = performance.now();
        if (now - lastQualityReportRef.current > 1000) {
          lastQualityReportRef.current = now;
          qualityWarningsRef.current = warnings;
          onQualityWarning(warnings);
        }
      }
      // ── End quality checks ─────────────────────────────────────────────────

      const needsRgbValues = !isSeeVitaModel || isRecordingRef.current;
      const rgbValues = needsRgbValues
        ? faceLandmarks.map((lm: any) => {
            const x = Math.floor(lm.x * tempCanvas.width);
            const y = Math.floor(lm.y * tempCanvas.height);

            // Clamp coordinates to prevent out-of-bounds errors
            const clampedX = Math.max(0, Math.min(x, tempCanvas.width - 1));
            const clampedY = Math.max(0, Math.min(y, tempCanvas.height - 1));

            const pixelData = tempCtx?.getImageData(clampedX, clampedY, 1, 1).data;
            return {
              r: pixelData ? pixelData[0] : 0,
              g: pixelData ? pixelData[1] : 0,
              b: pixelData ? pixelData[2] : 0,
            };
          })
        : undefined;



      if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
        const landmarks = faceLandmarks.map((lm: any) => ({
          x: lm.x,
          y: lm.y,
          z: lm.z,
        }));
      
        // console.log('Mapped landmarks:', landmarks); // Debug log to verify
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          // console.log('Sending landmarks via WebSocket:', landmarks); // Debug log to verify
          socketRef.current.send(
            JSON.stringify({
              type: 'landmarks',
              data: landmarks,
              timestamp: Date.now(),
            })
          );
        }
      }
      
      
      
      

      if (mode === 'api') {
        const currentTime = Date.now();

        // Extract 72x72 face crop for SeeVita models
        let faceCrop: string | undefined;
        if (isSeeVitaModel && video) {
          const xs = faceLandmarks.map((lm: any) => lm.x * video.videoWidth);
          const ys = faceLandmarks.map((lm: any) => lm.y * video.videoHeight);
          const xMin = Math.min(...xs);
          const xMax = Math.max(...xs);
          const yMin = Math.min(...ys);
          const yMax = Math.max(...ys);
          // Square crop: side = max(faceW, faceH) * scale, centred on face — matches Navid's pipeline
          const CROP_SCALE = isHiResCropModel ? 1.5 : (1 + 2 * cropPadFactor);
          const rawCx   = (xMin + xMax) / 2;
          const rawCy   = (yMin + yMax) / 2;
          const rawSide = Math.max(xMax - xMin, yMax - yMin) * CROP_SCALE;
          // EMA smoothing (α=0.85) to suppress frame-to-frame jitter
          const EMA_ALPHA = isHiResCropModel ? 0.85 : 0;
          const ema = cropEmaRef.current;
          const smCx   = ema ? EMA_ALPHA * rawCx   + (1 - EMA_ALPHA) * ema.cx   : rawCx;
          const smCy   = ema ? EMA_ALPHA * rawCy   + (1 - EMA_ALPHA) * ema.cy   : rawCy;
          const smSide = ema ? EMA_ALPHA * rawSide + (1 - EMA_ALPHA) * ema.side : rawSide;
          cropEmaRef.current = {cx: smCx, cy: smCy, side: smSide};
          const half  = smSide / 2;
          const cropX = Math.max(0, Math.round(smCx - half));
          const cropY = Math.max(0, Math.round(smCy - half));
          const cropW = Math.min(video.videoWidth  - cropX, Math.round(smSide));
          const cropH = Math.min(video.videoHeight - cropY, Math.round(smSide));
          if (!cropCanvasRef.current) {
            cropCanvasRef.current = document.createElement('canvas');
          }
          const cropCanvas = cropCanvasRef.current;
          cropCanvas.width = cropSize;
          cropCanvas.height = cropSize;
          const cropCtx = cropCanvas.getContext('2d');
          if (cropCtx && cropW > 0 && cropH > 0) {
            cropCtx.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, cropSize, cropSize);
            faceCrop = isHiResCropModel
              ? cropCanvas.toDataURL('image/png').split(',')[1]
              : cropCanvas.toDataURL('image/jpeg', 0.15).split(',')[1];
          }
        }

        // RhythmNet streaming mode: push this frame straight over the socket
        // (~30Hz, server paces inference to its own GPU-bound rate) instead of
        // waiting for a full chunk. The chunk/slide bookkeeping below still
        // runs for the progress bar and as the live HTTP fallback target.
        if (isRhythmNetModel && rnTransportActiveRef.current === 'ws' && faceCrop) {
          const ws = rnWsRef.current;
          if (ws && ws.readyState === WebSocket.OPEN) {
            lastWsFrameTimestampRef.current = currentTime;
            ws.send(JSON.stringify({ faceCrop, timestamp: currentTime }));
          }
        }

        const framePayload: FrameData = isSeeVitaModel
          ? { timestamp: currentTime, faceCrop }
          : { timestamp: currentTime, rgbValues };
        framesChunkRef.current.push(framePayload);
        if (framesChunkRef.current.length > chunkSize) {
          framesChunkRef.current.shift();
        }
        const collected = framesChunkRef.current.length;
        reportInferenceProgress(collected);
        slideCountRef.current++;
        if (collected >= chunkSize && slideCountRef.current >= SLIDE_INTERVAL) {
          slideCountRef.current = 0;
          if (!(isRhythmNetModel && rnTransportActiveRef.current === 'ws')) {
            sendToApi({ frames: [...framesChunkRef.current] });
          }
        }



      }
      

      if (isRecordingRef.current) {
        const currentTime = Date.now();

        setRecordedData((prevData) => ({
          frames: [
                ...prevData.frames,
                {
                  timestamp: currentTime, // Save the timestamp for the frame
                  rgbValues, // Save RGB values for all landmarks
                },
          ],
        }));
      }

      frameCountRef.current++;

      ctx.clearRect(0, 0, dispW, dispH);
      // Map landmarks from video-pixel space → canvas display space (normalized to canvas dims)
      const dispLandmarks = faceLandmarks.map((lm: any) => ({
        x: (lm.x * vW * cvScale + cvOffX) / dispW,
        y: (lm.y * vH * cvScale + cvOffY) / dispH,
        z: lm.z,
      }));
      drawFaceGuideOval(ctx, dispW, dispH, dispLandmarks, source === 'webcam');
      const dotR = Math.max(1, Math.round(dispW * 0.004));
      ctx.save();
      ctx.fillStyle = 'rgba(96,165,250,0.55)';
      for (const dlm of dispLandmarks) {
        ctx.beginPath();
        ctx.arc(dlm.x * dispW, dlm.y * dispH, dotR, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    } else {
      // No face detected — show static white oval so user knows where to position
      if (ctx && canvas && video) {
        ctx.clearRect(0, 0, dispW, dispH);
        drawFaceGuideOval(ctx, dispW, dispH, null, source === 'webcam');
      }
      noFaceFrameCountRef.current += 1;
      
      // If no face detected for too long, stop processing (scale with chunk size so BPFormer isn't killed early)
      const noFaceThreshold = Math.max(90, Math.round(chunkSize / 3));
      if (noFaceFrameCountRef.current >= noFaceThreshold && isProcessingRef.current) {
        console.warn('No face detected for 30+ frames. Stopping processing.');
        setIsProcessing(false);
        setIsRecording(false);
        setStoppedDueToNoFace(true); // Set flag to show message
        onProcessingStopped?.(true); // Notify parent that processing stopped
        onMediaPipeError?.(true); // Notify parent of MediaPipe failure
        reportInferenceProgress(0);
        noFaceFrameCountRef.current = 0; // Reset counter
      } else if (noFaceFrameCountRef.current % 10 === 0) {
        // Log warning every 10 frames to avoid spam
        console.warn(`No face landmarks detected in this frame. (${noFaceFrameCountRef.current} consecutive frames without face)`);
      }
    }
  } catch (error) {
    console.error('Error processing results:', error);
  }
};
handleResultsRef.current = handleResults;

  
  const toggleRecordingAndProcessing = () => {
    if (mode === 'stream') {
      if (isRecordingRef.current) {
        // Stop WebSocket and video streaming
        stopVideoStreaming();
        stopWebSocketConnection();
      } else {
        // Start WebSocket and video streaming
        startWebSocketConnection();
        startVideoStreaming();
      }
      setIsRecording((prev) => !prev);
      setIsProcessing((prev) => !prev);
    }
    else {
      if (!isRecordingRef.current) {
        framesChunkRef.current = [];
        slideCountRef.current = 0;
        pendingChunkRef.current = null;
        reportInferenceProgress(0);
      }
      setIsRecording((prev) => !prev);
      setIsProcessing((prev) => !prev);

      if (!isRecordingRef.current) {
        startTimeRef.current = performance.now();
      } else {
        setRecordingTime(0);
        framesChunkRef.current = [];
        slideCountRef.current = 0;
        pendingChunkRef.current = null;
        reportInferenceProgress(0);
  
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d', { willReadFrequently: true });
        const video = webcamRef.current?.video;
        if (ctx && video) {
          ctx.clearRect(0, 0, video.videoWidth, video.videoHeight);
        }
      }
    }
  };
  

  const toggleInference = () => {
    // setIsRecording((prev) => !prev);
    // setIsProcessing((prev) => !prev);
    toggleRecordingAndProcessing();
  }
  
  
  

  const formatTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const seconds = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
    const milliseconds = ((totalSeconds % 1) * 100).toFixed(0).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}.${milliseconds}`;
  };
  

  
  const downloadRecordedData = () => {
    try {
      if (recordedData.frames.length > 0) {
        // Prepare the data for saving
        const dataToSave = recordedData.frames.map((frame) => ({
          timestamp: new Date(frame.timestamp).toISOString(), // Convert frame timestamp to ISO format
          rgbValues: frame.rgbValues, // Include RGB values if recorded
        }));
  
        const blob = new Blob([JSON.stringify(dataToSave, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'recorded_data.json';
        link.click();
        URL.revokeObjectURL(url);
      } else {
        alert('No data recorded!');
      }
    } catch (error) {
      console.error('Error saving recorded data:', error);
    }
  };
  
  
  
  


  return (
    <div
    className="card webcam-card"
    style={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '100%',
      padding: hideControls ? '0' : '20px',
      backgroundColor: hideControls ? 'transparent' : '#ffffff',
      borderRadius: '10px',
      boxShadow: hideControls ? 'none' : '0 4px 8px rgba(0, 0, 0, 0.1)',
    }}
  >
      {!hideControls && <h3>Webcam Feed</h3>}
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {source === 'webcam' ? (
      <Webcam
        ref={webcamRef}
        mirrored={true}
        videoConstraints={{
          facingMode: 'user',
          frameRate: strictFrameRate ? { ideal: 30, min: 24, max: 30 } : { ideal: 30, max: 30 },
          aspectRatio: { ideal: 16 / 9 },
          width: { ideal: 1280, min: 320 },
          height: { ideal: 720, min: 240 },
        }}
        onUserMediaError={() => {
          if (strictFrameRate) {
            console.warn('Camera rejected the min-24fps constraint — retrying without a frame-rate floor.');
            setStrictFrameRate(false);
          }
        }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    ) : (
      <video
      autoPlay
      muted
      ref={videoRef}
      src={videoSrc}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      playsInline
      loop={source === 'video'}
    />
  )}
<canvas
  ref={canvasRef}
  style={{
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'transparent',
    transform: source === 'webcam' ? 'scaleX(-1)' : 'none',
  }}
/>
{stoppedDueToNoFace && (
  <div
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'rgba(220, 53, 69, 0.9)',
      color: 'white',
      padding: '20px 30px',
      borderRadius: '10px',
      fontSize: '18px',
      fontWeight: 'bold',
      textAlign: 'center',
      zIndex: 1000,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
      maxWidth: '80%',
    }}
  >
    ⚠️ Processing Stopped
    <br />
    <span style={{ fontSize: '14px', fontWeight: 'normal', display: 'block', marginTop: '8px' }}>
      No face detected.
      <br />
      Please reload the page and position your face in front of the camera to start monitoring.
    </span>
  </div>
)}

      </div>
      {!hideControls && (
        <>
          <div style={{ marginTop: '20px', fontSize: '18px', color: '#555' }}>
            <p><strong>Recording Time:</strong> {formatTime(recordingTime)}</p>
            <p><strong>FPS:</strong> {fps}</p>
          </div>
          <button
            onClick={toggleRecordingAndProcessing}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: isRecording ? '#FF0000' : '#28A745',
              color: 'white',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
            }}
          >
            {isRecording ? 'Stop Recording' : 'Start Recording'}
          </button>
        </>
      )}


    </div>
  );
}
);

export default WebcamCard;
